/**
 * useContactsEditor — a contacts editor's state: useRowsEditor over
 * record_contacts rows, in their stored order. A contact's value, label and
 * primary flag change in place; its kind is fixed once it's stored (a
 * different kind is a different contact), so only a new one picks a kind.
 *
 * One contact of a kind is primary: marking one unmarks the others of its
 * kind here, each an in-place edit of its row, as the dedicated set-primary
 * path does.
 */
import { type Field } from "@auger/core/fields"
import type { ContactKind, ContactRow } from "@auger/ui/utils/contacts"
import { ADD, type DraftRow, useRowsEditor } from "./useRowsEditor"

export { type RowsDelta as ContactsDelta } from "./useRowsEditor"

/** The kind of contact a field holds; socials are links. */
const FIELD_KINDS: Record<string, ContactKind> = {
  email: "email",
  phone: "phone",
  url: "url",
  location: "location",
  socials: "url",
}

/** The kind a field holds, or null where any kind goes (a generic contact). */
export const contactKindOf = (field: Pick<Field, "type">): ContactKind | null =>
  FIELD_KINDS[field.type] ?? null

export function useContactsEditor(rows: ContactRow[], field: Pick<Field, "type" | "isArray">) {
  const kind = contactKindOf(field)
  const editor = useRowsEditor<ContactRow>(rows, {
    columns: ["value", "label", "is_primary"],
    toEntity: (row) => ({
      contactType: row.contact_type,
      value: row.value,
      ...(row.label ? { label: row.label } : {}),
      ...(row.is_primary ? { isPrimary: true } : {}),
    }),
    blank: () => ({ contact_type: kind ?? "email", value: "", label: null }),
    isComplete: (row) => !!row.value,
    max: field.isArray ? undefined : 1,
  })

  /** Unmark every other primary contact of `primary`'s kind — `except` is the
   *  line being marked (a row's key, or ADD for the new contact). */
  function unmarkOthers(primary: ContactRow, except: string) {
    for (const row of editor.listed.value) {
      if (row.key === except || !row.cur.is_primary) continue
      if (row.cur.contact_type !== primary.contact_type) continue
      editor.setRow(row, { ...row.cur, is_primary: false })
    }
    const pending = editor.pending.value
    if (except !== ADD && pending.is_primary && pending.contact_type === primary.contact_type) {
      editor.pending.value = { ...pending, is_primary: false }
    }
  }

  function setRow(row: DraftRow<ContactRow>, value: ContactRow) {
    editor.setRow(row, value)
    if (value.is_primary) unmarkOthers(value, row.key)
  }

  function setPending(value: ContactRow) {
    editor.setPending(value)
    if (value.is_primary) unmarkOthers(value, ADD)
  }

  return { ...editor, setRow, setPending }
}
